import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus } from "lucide-react";
import { supabase } from "../../../supabase/supabase";
import { toast } from "@/components/ui/use-toast";

interface CreateOrganizationButtonProps {
  variant?:
    | "default"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "destructive";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  onOrganizationCreated?: () => void;
  organizationId?: string;
}

export default function CreateOrganizationButton({
  variant = "default",
  size = "default",
  className = "",
  onOrganizationCreated,
  organizationId,
}: CreateOrganizationButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const createOrganization = async () => {
    if (!orgName.trim()) return;

    try {
      setIsLoading(true);

      // Get the current session token
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error("No active session found");
      }

      // Prepare request body with organization ID if provided
      const requestBody: Record<string, any> = { name: orgName };

      // Only add parent_organization_id if it's provided
      if (organizationId) {
        requestBody.parent_organization_id = organizationId;
      }

      // Call the create_organization edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create_organization`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      const result = await response.json();
      console.log("Organization creation result:", result);

      if (!response.ok) {
        throw new Error(result?.error || "Failed to create organization");
      }

      toast({
        title: "Organization created",
        description: `${orgName} has been created successfully`,
      });

      setOrgName("");
      setIsDialogOpen(false);

      if (onOrganizationCreated) {
        onOrganizationCreated();
      }

      // Reload the page to update the organization context
      window.location.reload();
    } catch (error) {
      console.error("Error creating organization:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to create organization",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={() => setIsDialogOpen(true)}
      >
        <Plus className="h-4 w-4 mr-2" /> New Organization
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                placeholder="Enter organization name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && orgName.trim()) {
                    createOrganization();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createOrganization}
              disabled={!orgName.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
